import { BaseSyntheticEvent, useEffect, useRef } from "react";
import { useFirebase } from "../FbContex";

/**
 * Writes device state, debounced per `addr/key`.
 *
 * Replaces the old `ch()` in room.tsx, which had two bugs: its `let timer` was
 * re-declared on every render (so clearTimeout always cleared undefined and
 * every slider tick hit RTDB), and it ran parseInt over everything (so a
 * headless-ui Switch, whose onChange yields a boolean rather than an event,
 * wrote NaN).
 */
export function useSetter() {
  const ctx = useFirebase();
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, []);

  const setValue = (
    addr: string,
    key: string,
    value: number | boolean,
    delay?: number
  ) => {
    const path = `${addr}/${key}`;
    const write = () => {
      timers.current.delete(path);
      ctx.update({ [path]: value });
    };
    const existing = timers.current.get(path);
    if (existing) clearTimeout(existing);
    if (delay) timers.current.set(path, setTimeout(write, delay));
    else write();
  };

  /** For <input type="range"> / <input type="number"> onChange. */
  const onRangeChange =
    (addr: string, key: string, delay = 200) =>
    (e: BaseSyntheticEvent) => {
      const parsed = parseInt(String(e.target?.value), 10);
      if (Number.isNaN(parsed)) return;
      setValue(addr, key, parsed, delay);
    };

  const disabled = !ctx.connected && !ctx.local[0];

  return { setValue, onRangeChange, disabled };
}
